import { useState, useEffect } from 'react';
import { X, RefreshCw, StopCircle, Image as ImageIcon } from 'lucide-react';
import ScanProgress from './ScanProgress';
import { scannerAPI } from '../../../services/scannerService';
import toast from 'react-hot-toast';

interface ScanJobDetailProps {
  jobId: number;
  onClose: () => void;
  onRefresh: () => void;
}

export default function ScanJobDetail({ jobId, onClose, onRefresh }: ScanJobDetailProps) {
  const [job, setJob] = useState<any>(null);
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    loadJobDetails();

    // Auto-refresh every 3 seconds if scanning
    const interval = setInterval(() => {
      if (job && ['scanning', 'uploading'].includes(job.status)) {
        loadJobDetails();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [jobId]);

  const loadJobDetails = async () => {
    try {
      const data = await scannerAPI.getJobDetail(jobId);
      setJob(data);
      if (data.files) {
        setFiles(data.files);
      }
    } catch (error) {
      console.error('Failed to load job details:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleStopScan = async () => {
    if (!confirm('Are you sure you want to stop this scan?')) {
      return;
    }

    setStopping(true);
    try {
      const result = await scannerAPI.stopJob(jobId);
      if (result.success) {
        toast.success('Scan stopped');
        loadJobDetails();
        onRefresh();
      } else {
        toast.error(result.error || 'Failed to stop scan');
      }
    } catch (error: any) {
      console.error('Failed to stop scan:', error);
      toast.error(error.message || 'Failed to stop scan');
    } finally {
      setStopping(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN');
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
        </div>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Scan Job Details</h2>
            <p className="text-sm text-gray-500 mt-1">{job.job_id}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Progress */}
          {['scanning', 'uploading'].includes(job.status) && (
            <div className="mb-6">
              <ScanProgress job={job} />
            </div>
          )}

          {/* Job Info Grid */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">Patient Information</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-600">Patient ID:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {job.patient_id || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Patient Name:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {job.patient_name || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Case ID:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {job.case || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">Scan Information</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-600">Device:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {job.device_name}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Scan Mode:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {job.scan_mode}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className="ml-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      job.status === 'completed' ? 'bg-green-100 text-green-800' :
                      job.status === 'scanning' ? 'bg-blue-100 text-blue-800' :
                      job.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {job.status_display}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">Timing</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-600">Started:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {formatDate(job.scan_started_at)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Completed:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {formatDate(job.scan_completed_at)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Duration:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {job.duration ? `${Math.floor(job.duration / 60)}m ${Math.floor(job.duration % 60)}s` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">Progress Statistics</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-600">Tiles:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {job.scanned_tiles} / {job.total_tiles || '?'}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Files:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {job.uploaded_files} / {job.total_files || '?'}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Initiated by:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {job.initiated_by_username}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {job.error_message && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-sm font-medium text-red-800 mb-1">Error</h3>
              <p className="text-sm text-red-600">{job.error_message}</p>
            </div>
          )}

          {/* Files List */}
          {files.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">
                Scan Files ({files.length})
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                <div className="space-y-2">
                  {files.slice(0, 50).map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white rounded hover:bg-gray-50 transition"
                    >
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{file.filename}</span>
                        {file.is_uploaded && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                            Uploaded
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatFileSize(file.file_size)}
                      </span>
                    </div>
                  ))}
                  {files.length > 50 && (
                    <p className="text-sm text-gray-500 text-center pt-2">
                      ... and {files.length - 50} more files
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t p-6">
          <div className="flex gap-3 justify-end">
            {['scanning', 'uploading', 'pending'].includes(job.status) && (
              <button
                onClick={handleStopScan}
                disabled={stopping}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-400 flex items-center gap-2"
              >
                {stopping ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Stopping...
                  </>
                ) : (
                  <>
                    <StopCircle className="w-4 h-4" />
                    Stop Scan
                  </>
                )}
              </button>
            )}
            <button
              onClick={loadJobDetails}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
